<template>
  <component
    :is="isCardLink ? 'a' : 'div'"
    :href="isCardLink ? settingsEnergyUrl : false"
    class="mp-customize-card"
  >
    <div class="message">
      <!-- error state -->
      <callout v-if="error" type="error">
        <div>{{ error }}</div>
      </callout>

      <!-- header, only for card view -->
      <h2 v-if="(isReady || loading) && showHeader" class="heading -primary">
        <span class="personalize-heading">Personalize</span>
        <span class="sub-heading">Your Meal Plans</span>
      </h2>

      <!-- energy meter and empty loading state -->
      <energy-meter
        v-if="isReady"
        :daily-calorie-type="hasEnergySettings ? settings.energy.dailyCalorieType : 4"
        :calorie-types="calorieTypes"
      />
      <energy-meter
        v-if="loading"
        :daily-calorie-type="0"
        :calorie-types="{ 1: '', 2: '', 3: '', 4: '', 5: '', 6: '', 7: '', 8: '' }"
      />

      <!-- selection content -->
      <div v-if="isReady && hasEnergySettings" class="current-selection">
        <span v-if="settings.energy.dietaryMethodName">
          <strong>{{ settings.energy.dietaryMethodName }}</strong
          ><br />
        </span>

        <!-- toggle for range vs cal display -->
        <strong
          v-if="settings.energy.energyMethod === 'range' && settings.energy.dailyCalorieTypeInfo"
        >
          {{ settings.energy.dailyCalorieTypeInfo.name }}
        </strong>
        <strong
          v-if="settings.energy.energyMethod === 'goal' && settings.energy.dailyCalorieTypeInfo"
          >{{ settings.energy.dailyCalorieTypeInfo.name }}</strong
        >
        <strong
          v-else-if="
            settings.energy.energyMethod === 'calculator' && settings.energy.dailyCalorieTypeInfo
          "
        >
          {{ settings.energy.dailyCalorieTypeInfo.calMin }}-{{
            settings.energy.dailyCalorieTypeInfo.calMax
          }}
          Calories
        </strong>
      </div>
      <div v-if="isReady && !hasEnergySettings" class="current-selection">
        <b>Default Settings</b>
      </div>

      <!-- selection content, loading state -->
      <div v-if="loading" class="current-selection">
        <b>Loading</b><br />
        <b>Settings</b>
      </div>

      <!-- card actions, buttonLink and chevron toggle -->
      <div v-if="isReady && showActions === 'buttonLink'" class="card-action">
        <a :href="settingsEnergyUrl" class="btn -main">
          {{ hasEnergySettings ? 'Change Settings' : 'Personalize' }}
        </a>
      </div>
      <div v-else-if="isReady && showActions === 'chevron'" class="card-action">
        <i class="material-symbols-outlined" aria-hidden="true">chevron_right</i>
      </div>

      <!-- action, hold space if loading -->
      <div v-if="loading" class="card-action"></div>

      <!--* anything extra -->
      <slot></slot>
    </div>
  </component>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import EnergyMeter from './energy-meter.vue'
import { fbApi, objToSearchParams } from '@/utils/http-api'

/**
 * Base energy card from user settings.
 * - fetches users energy settings on create
 * - emits event once settings are received
 */
export default defineComponent({
  name: 'EnergyCard',
  components: {
    EnergyMeter,
  },
  props: {
    isCardLink: {
      type: Boolean,
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showActions: {
      type: String,
      default: 'buttonLink',
      validator: (val) => ['buttonLink', 'chevron', ''].includes(val),
    },
    // default goes to whatever the current URL + query strings are
    from: {
      type: String,
      default: encodeURIComponent(`${window.location.pathname}${window.location.search}`),
    },
    fromName: {
      type: String,
      default: '',
    },

    // if two energy cards are on the page at once, pass in calorie types
    // and disable fetch to avoid an XHR per energy card component
    initialCalorieTypes: {
      type: Object,
      default: () => ({}),
    },
    fetchDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['energy-loaded'],
  data() {
    return {
      loading: false,
      error: '',

      // set from props if passed in
      calorieTypes: this.initialCalorieTypes,
    }
  },
  computed: {
    ...mapState(useUserAuthStore, ['settings']),

    isReady() {
      return !this.loading && !this.error && this.settings && this.settings.energy
    },

    hasEnergySettings() {
      return this.settings && this.settings.energy && this.settings.energy.dailyCalorieType
    },

    settingsEnergyUrl() {
      return `/my/settings/energy?from=${this.from}&fromName=${this.fromName}`
    },
  },

  watch: {
    // disable loading once energy is in pinia if fetch Disabled
    'settings.energy': function () {
      if (this.fetchDisabled && this.loading) {
        this.loading = false
      }
    },
  },

  created() {
    this.fetchSettings()

    // if fetch is disabled, set loading to force loading state
    // as a second card is fetching... wait for data to load before disabling
    if (this.fetchDisabled) {
      this.loading = true
    }
  },

  methods: {
    ...mapActions(useUserAuthStore, ['updateSettings']),

    /**
     * Fetch user settings for energy and calorie ranges
     */
    async fetchSettings() {
      if (this.fetchDisabled || this.loading === true) {
        return
      }

      // resets
      this.loading = true
      this.error = ''

      try {
        const res = await fbApi.get('/my/settings', {
          searchParams: objToSearchParams({
            energy: 1,
          }),
        })

        // missing data
        if (!res || !res.body || !res.body.data) {
          throw new Error(`Unable to fetch energy settings`)
        }

        const { data } = res.body

        if (!data.options || !data.options.calorieTypes || !data.options.activityLevels) {
          throw new Error(`Unable to fetch energy setting options`)
        }
        // end checks

        // set drop down options from API, overriding anything from initialCalorieTypes prop
        this.calorieTypes = data.options.calorieTypes

        // update pinia energy settings
        const energy = data.settings.energy ? data.settings.energy : {}
        this.updateSettings({ energy })

        this.$emit('energy-loaded')
      } catch (err) {
        console.log(err)
        this.error = `Unable to fetch energy settings`
      } finally {
        this.loading = false
      }
    },
  },
})
</script>
